import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import manageVideo from "../../assets/videos/en/Manage.mp4";
import buildVideo from "../../assets/videos/en/Build.mp4";
import infoVideo from "../../assets/videos/en/Info.mp4";
import continuationVideo from "../../assets/videos/en/Continuation.mp4";
import aiVideo from "../../assets/videos/en/AI.mp4";
import recordVideo from "../../assets/videos/en/Record.mp4";
import teamVideo from "../../assets/videos/en/Team.mp4";
import madgicxLogo from "../../assets/images/Comparison/madgicx.svg";
import smartlyLogo from "../../assets/images/Comparison/smartly.svg";
import ndaBadge from "../../assets/images/Home/protect/nda.png";
import ccpaBadge from "../../assets/images/Home/protect/ccpa.png";
import eprivacysealBadge from "../../assets/images/Home/protect/eprivacyseal.png";
import aicpaBadge from "../../assets/images/Home/protect/aicpa.png";
import comparisonPages, {
  complianceBadges,
  customerLogoRows,
  platformLogoRows,
  stats,
  trialPerks,
} from "./config";
import "./index.scss";

const videoSources = {
  Manage: manageVideo,
  Build: buildVideo,
  Info: infoVideo,
  Continuation: continuationVideo,
  AI: aiVideo,
  Record: recordVideo,
  Team: teamVideo,
};

const competitorLogos = {
  madgicx: madgicxLogo,
  smartly: smartlyLogo,
};

const badgeSources = {
  nda: ndaBadge,
  ccpa: ccpaBadge,
  eprivacyseal: eprivacysealBadge,
  aicpa: aicpaBadge,
};

const getPageKey = (pathname) =>
  pathname.replace(/\/+$/, "").endsWith("xmp-vs-smartly")
    ? "smartly"
    : "madgicx";

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  const wasCreated = !element;
  const previous = {};

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    previous[name] = element.getAttribute(name);
    element.setAttribute(name, value);
  });

  return () => {
    if (wasCreated) {
      element.remove();
      return;
    }

    Object.entries(previous).forEach(([name, value]) => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    });
  };
};

function useComparisonMetadata(page) {
  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const canonicalUrl = `https://xmp.mobvista.com${page.path}`;
    const cleanups = [
      upsertMeta('meta[name="description"]', {
        name: "description",
        content: page.description,
      }),
      upsertMeta('meta[name="keywords"]', {
        name: "keywords",
        content: page.keywords,
      }),
      upsertMeta('meta[name="robots"]', {
        name: "robots",
        content: "index, follow, max-snippet:-1, max-image-preview:large",
      }),
      upsertMeta('meta[property="og:type"]', {
        property: "og:type",
        content: "article",
      }),
      upsertMeta('meta[property="og:title"]', {
        property: "og:title",
        content: page.title,
      }),
      upsertMeta('meta[property="og:description"]', {
        property: "og:description",
        content: page.description,
      }),
      upsertMeta('meta[property="og:url"]', {
        property: "og:url",
        content: canonicalUrl,
      }),
      upsertMeta('meta[property="og:site_name"]', {
        property: "og:site_name",
        content: "XMP",
      }),
      upsertMeta('meta[name="twitter:card"]', {
        name: "twitter:card",
        content: "summary_large_image",
      }),
      upsertMeta('meta[name="twitter:title"]', {
        name: "twitter:title",
        content: page.title,
      }),
      upsertMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: page.description,
      }),
    ];

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalWasCreated = !canonical;
    const previousCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const structuredData = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: page.headline,
        description: page.description,
        datePublished: "2026-05-14",
        author: { "@type": "Organization", name: "XMP" },
        publisher: {
          "@type": "Organization",
          name: "XMP",
          url: "https://xmp.mobvista.com",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faq.map(([question, answer]) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "XMP",
            item: "https://xmp.mobvista.com/en",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: `XMP vs ${page.name}`,
            item: canonicalUrl,
          },
        ],
      },
    ];
    const hasServerSchemas = Array.from(
      document.head.querySelectorAll("script[data-comparison-server-schema]")
    ).some((node) => {
      try {
        JSON.parse(node.textContent);
        return true;
      } catch {
        return false;
      }
    });
    const schemaNodes = hasServerSchemas
      ? []
      : structuredData.map((schema, index) => {
          const node = document.createElement("script");
          node.type = "application/ld+json";
          node.dataset.comparisonSchema = `${page.slug}-${index}`;
          node.textContent = JSON.stringify(schema);
          document.head.appendChild(node);
          return node;
        });

    document.title = page.title;
    document.documentElement.lang = "en";

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      schemaNodes.forEach((node) => node.remove());
      if (canonicalWasCreated) canonical.remove();
      else if (previousCanonical === null) canonical.removeAttribute("href");
      else canonical.setAttribute("href", previousCanonical);
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
    };
  }, [page]);
}

function usePageInteractions(rootRef, page, setActiveCapability) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    window.scrollTo(0, 0);
    setActiveCapability(page.capabilities[0].id);

    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const observers = [];
    const animationFrames = new Set();
    const revealItems = root.querySelectorAll("[data-comparison-reveal]");

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      revealItems.forEach((item) => item.classList.add("is-visible"));
    } else {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      revealItems.forEach((item) => revealObserver.observe(item));
      observers.push(revealObserver);
    }

    if (!prefersReducedMotion && "IntersectionObserver" in window) {
      const statsObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.target.dataset.counted) return;
            const element = entry.target;
            const endValue = Number(element.dataset.end);
            const formatValue = (value) =>
              element.dataset.format === "locale"
                ? value.toLocaleString()
                : String(value);
            element.dataset.counted = "true";
            const startTime = performance.now();
            const draw = (now) => {
              const progress = Math.min((now - startTime) / 900, 1);
              const value = Math.round(endValue * (1 - Math.pow(1 - progress, 3)));
              element.textContent = formatValue(value);
              if (progress < 1) {
                const frame = window.requestAnimationFrame(draw);
                animationFrames.add(frame);
              }
            };
            element.textContent = formatValue(0);
            const frame = window.requestAnimationFrame(draw);
            animationFrames.add(frame);
            statsObserver.unobserve(element);
          });
        },
        { threshold: 0.55 }
      );
      root
        .querySelectorAll(".comparison-stat-value")
        .forEach((item) => statsObserver.observe(item));
      observers.push(statsObserver);
    }

    if ("IntersectionObserver" in window) {
      const capabilityBlocks = Array.from(
        root.querySelectorAll(".comparison-capability")
      );
      const capabilityObserver = new IntersectionObserver(
        (entries) => {
          const visibleEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visibleEntry) setActiveCapability(visibleEntry.target.id);
        },
        { threshold: [0.35, 0.55, 0.75], rootMargin: "-20% 0px -35% 0px" }
      );
      capabilityBlocks.forEach((block) => capabilityObserver.observe(block));
      observers.push(capabilityObserver);

      if (!prefersReducedMotion) {
        const videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) video.play().catch(() => {});
              else video.pause();
            });
          },
          { threshold: 0.15 }
        );
        root.querySelectorAll("video").forEach((video) => {
          video.muted = true;
          videoObserver.observe(video);
        });
        observers.push(videoObserver);
      }
    }

    return () => {
      observers.forEach((observer) => observer.disconnect());
      animationFrames.forEach((frame) => window.cancelAnimationFrame(frame));
    };
  }, [page, rootRef, setActiveCapability]);
}

function RichText({ parts }) {
  return parts.map((part, index) => {
    const className = part.accent ? "comparison-accent" : undefined;
    const content = part.strong ? (
      <strong className={className}>{part.text}</strong>
    ) : (
      <span className={className}>{part.text}</span>
    );

    return (
      <Fragment key={`${part.text}-${index}`}>
        {part.wideBreakBefore && (
          <br className="comparison-lede-wide-break" />
        )}
        {content}
      </Fragment>
    );
  });
}

function SectionHeading({ eyebrow, title, accent, description, centered = false }) {
  return (
    <div
      className={`comparison-section-heading${centered ? " is-centered" : ""}`}
    >
      <div className="comparison-eyebrow">{eyebrow}</div>
      <h2>
        {title} <span className="comparison-title-highlight">{accent}</span>
      </h2>
      {description && <p>{description}</p>}
    </div>
  );
}

function ComparisonHero({ page }) {
  return (
    <section className="comparison-hero">
      <div className="comparison-container comparison-hero-grid">
        <div className="comparison-hero-copy">
          <div className="comparison-eyebrow">XMP vs {page.name}</div>
          <p className="comparison-hero-greeting">
            <RichText parts={page.hero.greeting} />
          </p>
          <h1>
            {page.hero.titleLines.map((line) => (
              <span
                className={`${line.accent ? "comparison-accent" : ""}${
                  line.highlight ? " comparison-title-highlight" : ""
                }`}
                key={line.text}
              >
                {line.text}
              </span>
            ))}
          </h1>
          <p className="comparison-hero-lede">
            <RichText parts={page.hero.lede} />
          </p>
        </div>

        <aside className="comparison-trial-panel" aria-label="XMP free trial">
          <ul>
            {trialPerks.map((perk, index) => (
              <li key={index}>
                <RichText parts={perk} />
              </li>
            ))}
          </ul>
          <a className="comparison-primary-button" href={page.signupUrl}>
            Try XMP for free
          </a>
          <p>
            7-day free trial &middot; No credit card required &middot; Free tier for
            eligible teams &middot; No media-spend minimum
          </p>
        </aside>
      </div>
    </section>
  );
}

function LogoMarquee({ rows, type }) {
  const basePath = type === "platform" ? "brandList" : "companyList";
  return (
    <div className={`comparison-logo-wall comparison-logo-wall--${type}`} aria-hidden="true">
      {rows.map((row, rowIndex) => (
        <div className="comparison-logo-row" key={`${type}-${rowIndex}`}>
          <div className="comparison-logo-track">
            {[...row, ...row].map(([file, name], index) => (
              <div className="comparison-logo-card" key={`${file}-${index}`}>
                <img
                  src={file.startsWith("http") ? file : `/official/${basePath}/${file}`}
                  alt={name}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatsSection() {
  return (
    <section className="comparison-section comparison-stats-section">
      <div className="comparison-container comparison-stats-grid">
        {stats.map((stat) => (
          <div className="comparison-stat" data-comparison-reveal key={stat.label}>
            <div className="comparison-stat-number">
              {stat.prefix}
              <span
                className="comparison-stat-value"
                data-end={stat.value}
                data-format={stat.format || "plain"}
              >
                {stat.format === "locale" ? stat.value.toLocaleString() : stat.value}
              </span>
              {stat.suffix}
              {stat.unit && (
                <span className="comparison-stat-unit">{stat.unit}</span>
              )}
            </div>
            <div className="comparison-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function IndustryContext() {
  return (
    <section className="comparison-quote-band">
      <div className="comparison-container">
        <div className="comparison-quote-card">
          <p className="comparison-quote-eyebrow">Industry Context</p>
          <p className="comparison-quote-text">
            Meta. TikTok. Google. Apple Ads. Snapchat. Kwai. Petal Ads. AppLovin.
            Mintegral. Unity. Liftoff / Vungle.
            <br />
            These are the 11 channels XMP supports for mobile UA.
          </p>
          <p className="comparison-quote-punchline">XMP runs them all.</p>
          <a
            className="comparison-quote-source"
            href="https://www.appsflyer.com/resources/reports/performance-index/"
            target="_blank"
            rel="noreferrer"
          >
            AppsFlyer Performance Index
          </a>
        </div>
      </div>
    </section>
  );
}

function AudienceSection({ page }) {
  return (
    <section className="comparison-section">
      <div className="comparison-container">
        <div className="comparison-vs-logos" aria-label={`${page.name} vs XMP`}>
          <div>
            <img src={competitorLogos[page.slug]} alt={`${page.name} logo`} />
          </div>
          <span>VS</span>
          <div>
            <img src="/official/logo.svg" alt="XMP logo" />
          </div>
        </div>

        <SectionHeading
          eyebrow="Who is this for"
          title="Same category,"
          accent="different jobs."
          description={page.audienceIntro}
          centered
        />

        <div className="comparison-audience-grid">
          {page.audiences.map((audience, index) => (
            <article
              className={`comparison-audience-card${index === 1 ? " is-xmp" : ""}`}
              data-comparison-reveal
              key={audience.name}
            >
              <div className="comparison-audience-name">{audience.name}</div>
              <div className="comparison-audience-who">
                <RichText parts={audience.who} />
              </div>
              <p>{audience.description}</p>
              <dl>
                {audience.rows.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
          <div className="comparison-vs-badge" aria-hidden="true">
            VS
          </div>
        </div>
      </div>
    </section>
  );
}

function ComparisonCell({ value }) {
  const cell = typeof value === "string" ? { text: value } : value;
  return (
    <span
      className={`comparison-cell-content${cell.status ? " has-status" : ""}`}
    >
      {cell.status && (
        <span
          className={`comparison-status comparison-status--${cell.status}`}
          aria-label={cell.status === "yes" ? "Supported" : "Not supported"}
        />
      )}
      <span className="comparison-cell-text">{cell.text}</span>
    </span>
  );
}

function FeatureTable({ page }) {
  return (
    <section className="comparison-section comparison-feature-section">
      <div className="comparison-container">
        <SectionHeading
          eyebrow="Capability comparison"
          title="The features,"
          accent="side by side."
          description={page.tableIntro}
        />
        <div className="comparison-table-wrap" data-comparison-reveal>
          <table>
            <thead>
              <tr>
                <th>Capability</th>
                <th className="is-xmp">XMP</th>
                <th>{page.name}</th>
              </tr>
            </thead>
            <tbody>
              {page.comparisonRows.map(([feature, xmp, competitor, note]) => (
                <tr key={feature}>
                  <th scope="row">
                    {feature}
                    {note && <small>{note}</small>}
                  </th>
                  <td className="is-xmp">
                    <ComparisonCell value={xmp} />
                  </td>
                  <td>
                    <ComparisonCell value={competitor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CapabilitiesSection({ page, activeCapability, setActiveCapability }) {
  const tabsRef = useRef(null);

  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs || tabs.scrollWidth <= tabs.clientWidth) return;

    const activeItem = tabs.querySelector(
      `[data-capability-id="${activeCapability}"]`
    );
    if (!activeItem) return;

    tabs.scrollTo({
      left:
        activeItem.offsetLeft -
        (tabs.clientWidth - activeItem.offsetWidth) / 2,
      behavior: "smooth",
    });
  }, [activeCapability]);

  const scrollToCapability = (id) => {
    setActiveCapability(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section className="comparison-section comparison-capabilities-section">
      <div className="comparison-container">
        <SectionHeading
          eyebrow="What XMP does"
          title="Seven capabilities."
          accent="One workflow."
          description="Built around things performance teams actually do."
        />
        <nav
          ref={tabsRef}
          className="comparison-tabs"
          aria-label="XMP capabilities"
        >
          {page.capabilities.map((capability) => (
            <button
              type="button"
              aria-current={
                activeCapability === capability.id ? "true" : undefined
              }
              data-capability-id={capability.id}
              className={activeCapability === capability.id ? "is-active" : ""}
              onClick={() => scrollToCapability(capability.id)}
              key={capability.id}
            >
              {capability.tab}
            </button>
          ))}
        </nav>

        <div className="comparison-capability-list">
          {page.capabilities.map((capability) => (
            <article
              className="comparison-capability"
              id={capability.id}
              data-comparison-reveal
              key={capability.id}
            >
              <div className="comparison-capability-copy">
                <div className="comparison-capability-tag">{capability.tag}</div>
                <h3>
                  {capability.title}
                  {capability.titleAccent && (
                    <>
                      {" "}
                      <span className="comparison-accent">
                        {capability.titleAccent}
                      </span>
                    </>
                  )}
                </h3>
                <p>{capability.description}</p>
              </div>
              <div className="comparison-capability-media">
                <video
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                >
                  <source src={videoSources[capability.video]} type="video/mp4" />
                </video>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValueBanner({ page }) {
  return (
    <section className="comparison-value-banner" data-comparison-reveal>
      <div className="comparison-container">
        <div>
          <h2>
            {page.valueBanner.text} <span>{page.valueBanner.pill}</span>
          </h2>
          <a href={page.signupUrl}>Try XMP for free</a>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="comparison-trust-section" data-comparison-reveal>
      <div className="comparison-container">
        <h2>Trusted by leading app & game developers worldwide</h2>
        <p>From indie studios to top-grossing publishers, across 11 ad channels.</p>
      </div>
      <LogoMarquee rows={customerLogoRows} type="customer" />
      <div className="comparison-container comparison-compliance-grid">
        {complianceBadges.map(([key, label], index) => (
          <div className="comparison-compliance-entry" key={key}>
            {index > 0 && <span aria-hidden="true">+</span>}
            <div>
              <img src={badgeSources[key]} alt="" />
              <p>{label}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PricingSection({ page }) {
  return (
    <section className="comparison-section comparison-pricing-section">
      <div className="comparison-container">
        <SectionHeading
          eyebrow="Pricing"
          title={page.pricing.heading[0]}
          accent={page.pricing.heading[1]}
          description={page.pricing.description}
        />
        <div className="comparison-pricing-grid">
          {page.pricing.cards.map((card, index) => (
            <Fragment key={card.name}>
              <article
                className={`comparison-price-card${card.featured ? " is-featured" : ""}`}
                data-comparison-reveal
              >
                <div className="comparison-price-name">{card.name}</div>
                <div className="comparison-price-rate">
                  {card.rate}
                  <small>{card.rateDetail}</small>
                </div>
                <p>
                  {Array.isArray(card.note)
                    ? card.note.map((line, lineIndex) => (
                        <Fragment key={line}>
                          {lineIndex > 0 && <br />}
                          {line}
                        </Fragment>
                      ))
                    : card.note}
                </p>
                <ul>
                  {card.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </article>
              {index === 0 && (
                <span className="comparison-price-vs" aria-hidden="true">
                  VS
                </span>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection({ page }) {
  return (
    <section className="comparison-section comparison-faq-section">
      <div className="comparison-container">
        <SectionHeading eyebrow="FAQ" title="Common questions," accent="answered." />
        <div className="comparison-faq-list">
          {page.faq.map(([question, answer]) => (
            <details data-comparison-reveal key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function BottomLine({ page }) {
  return (
    <section className="comparison-quote-band comparison-bottom-line">
      <div className="comparison-container">
        <div className="comparison-quote-card">
          <p className="comparison-quote-eyebrow">The bottom line</p>
          <p className="comparison-quote-text">{page.bottomLine}</p>
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const { pathname } = useLocation();
  const pageKey = getPageKey(pathname);
  const page = comparisonPages[pageKey];
  const rootRef = useRef(null);
  const [activeCapability, setActiveCapability] = useState(
    page.capabilities[0].id
  );

  useComparisonMetadata(page);
  usePageInteractions(rootRef, page, setActiveCapability);

  return (
    <main
      ref={rootRef}
      className={`ComparisonPage ComparisonPage--${pageKey}`}
    >
      <ComparisonHero page={page} />
      <LogoMarquee rows={platformLogoRows} type="platform" />
      <StatsSection />
      <IndustryContext />
      <AudienceSection page={page} />
      <FeatureTable page={page} />
      <CapabilitiesSection
        page={page}
        activeCapability={activeCapability}
        setActiveCapability={setActiveCapability}
      />
      <ValueBanner page={page} />
      <TrustSection />
      <PricingSection page={page} />
      <FaqSection page={page} />
      <BottomLine page={page} />
    </main>
  );
}

export default Comparison;
